import React from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { userHasActiveEntitlement } from "../utils/entitlements";

function _resolveLang(pathname, countryParam) {
  if (countryParam && /^[a-z]{2}$/i.test(countryParam)) {
    return countryParam.toLowerCase();
  }
  const match = String(pathname || "").match(/^\/([a-z]{2})(?:\/|$)/i);
  if (match?.[1]) {
    return match[1].toLowerCase();
  }
  const stored = (localStorage.getItem("language") || "").slice(0, 2).toLowerCase();
  return /^[a-z]{2}$/.test(stored) ? stored : "el";
}

const ProtectedRoute = ({ children, requireAuth = false, requirePremium = false }) => {
  const location = useLocation();
  const { country } = useParams();
  const { user, loading } = useAuth() || {};

  const isHydrating = Boolean(loading);
  const isAuthenticated = Boolean(user);
  const hasPremium = Boolean(userHasActiveEntitlement(user));

  const lang = _resolveLang(location.pathname, country);
  const loginTarget = `/${lang}/login`;
  const subscriptionTarget = `/${lang}/subscription`;

  if (isHydrating) {
    return (
      <Box
        sx={{
          minHeight: "40vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.25 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Guard case 1: user must be authenticated.
  if ((requireAuth || requirePremium) && !isAuthenticated) {
    if (location.pathname === loginTarget) {
      return children;
    }
    return <Navigate to={loginTarget} replace state={{ from: location }} />;
  }

  // Guard case 2: user must have active entitlement.
  if (requirePremium && !hasPremium) {
    if (location.pathname === subscriptionTarget) {
      return children;
    }
    return <Navigate to={subscriptionTarget} replace state={{ from: location }} />;
  }

  // Guard case 3: access granted.
  return children;
};

export default ProtectedRoute;
